import { useState } from "react";
import {
  BG_CATALOG,
  PERSONA_CATALOG,
  PERSONA_IDS,
} from "./catalog";

interface Props {
  selected: string[];
  onChange: (next: string[]) => void;
  /** Currently selected backgrounds, used for hover composite preview. */
  bgIds: string[];
  /** Section number prefix (e.g. "01"). Default "01". */
  sectionNumber?: string;
  /** Allow zero selection. If false, prevents removing the last one. Default true. */
  allowEmpty?: boolean;
}

export default function PersonaPicker({
  selected,
  onChange,
  bgIds,
  sectionNumber = "01",
  allowEmpty = true,
}: Props) {
  const [hovered, setHovered] = useState<string | null>(null);
  const previewBgId = bgIds[0] ?? "fond1_bureau_vitre";

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      const next = selected.filter((x) => x !== id);
      if (next.length === 0 && !allowEmpty) return;
      onChange(next);
    } else {
      onChange([...selected, id]);
    }
  };

  const selectAll = () => onChange(PERSONA_IDS);
  const clear = () => onChange(allowEmpty ? [] : [PERSONA_IDS[0]]);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="font-mono text-[10.5px] uppercase text-concrete flex items-center gap-2">
          <span>{sectionNumber}</span>
          <span className="font-sans text-[15px] font-semibold text-charcoal normal-case">
            Personnages
          </span>
        </h2>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10.5px] text-concrete tabular-nums">
            {selected.length}/{PERSONA_CATALOG.length} sélectionné
            {selected.length > 1 ? "s" : ""}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={selectAll}
              className="px-2.5 py-1 text-[10.5px] font-mono uppercase border border-charcoal rounded hover:bg-charcoal hover:text-ivory transition"
            >
              Tout
            </button>
            <button
              type="button"
              onClick={clear}
              className="px-2.5 py-1 text-[10.5px] font-mono uppercase border border-stone rounded hover:border-charcoal transition"
            >
              Aucun
            </button>
          </div>
        </div>
      </div>
      <p className="text-xs text-concrete -mt-1">
        Survole une carte pour voir le rendu avec le fond sélectionné.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PERSONA_CATALOG.map((p) => {
          const on = selected.includes(p.id);
          const showComposite = hovered === p.id && bgIds.length > 0;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p.id)}
              onMouseEnter={() => setHovered(p.id)}
              onMouseLeave={() => setHovered(null)}
              className={`relative text-left rounded-2xl border overflow-hidden transition bg-paper ${
                on
                  ? "border-[2px] border-charcoal bg-ivory-tint shadow-sm"
                  : "border-stone hover:border-charcoal/40"
              }`}
            >
              {on && (
                <span className="absolute top-3 right-3 z-20 w-7 h-7 rounded-full bg-charcoal text-ivory text-sm font-mono flex items-center justify-center shadow">
                  ✓
                </span>
              )}
              <div
                className="aspect-square w-full bg-stone/30 relative"
                style={
                  showComposite
                    ? {
                        backgroundImage: `url(/avatar-assets/backgrounds/${previewBgId}.jpg)`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }
                    : undefined
                }
              >
                <img
                  src={
                    showComposite
                      ? `/avatar-assets/persons-matted/${p.id}.png`
                      : `/avatar-assets/persons/${p.id}.jpg`
                  }
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                {showComposite && (
                  <span className="absolute bottom-2 left-2 font-mono text-[10px] uppercase tracking-wide bg-charcoal/80 text-ivory px-1.5 py-0.5 rounded">
                    sur {BG_CATALOG.find((b) => b.id === previewBgId)?.name}
                  </span>
                )}
              </div>
              <div className="p-4 flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-sans text-xl font-semibold leading-tight">
                    {p.name}
                  </h3>
                  <span className="font-mono text-[10.5px] text-concrete shrink-0">
                    {p.langs.join(" · ")}
                  </span>
                </div>
                <div className="font-mono text-[10.5px] uppercase tracking-wide text-concrete">
                  {p.role} · {p.age}
                </div>
                <p className="text-sm text-charcoal leading-snug">{p.pitch}</p>
                <p className="text-xs text-concrete leading-snug italic">
                  Idéal pour : {p.usage}
                </p>
                <div className="mt-1 flex items-center gap-1.5 text-[10.5px] font-mono text-concrete">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-charcoal/60" />
                  <span>Voix {p.voice}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
